<template>
    <v-app-bar
        class="header"
        :color="appBarBackground"
        dark
        app
        fixed
    >
        <div class="left-btn-container">
            <v-btn
                v-if="refreshing !== 'done'"
                icon
                color="white"
                :loading="refreshing === 'inProgress'"
                @click="refresh"
            >
                <v-icon>fal fa-redo</v-icon>
            </v-btn>
        </div>

        <v-tabs
            v-model="currentTab"
            class="tabs"
            background-color="transparent"
            slider-color="yellow"
            fixed-tabs
        >
            <v-tab
                v-for="(sector, index) in sectors"
                :key="sector.id"
                :class="{ 'first-tab': index == 0 }"
                :href="`#tab-${index}`"
            >
                {{ sector.name }}
            </v-tab>
        </v-tabs>

        <!-- <v-tooltip bottom>
            <template v-slot:activator="{ on }">
                <v-btn
                    icon
                    color="white"
                    v-on="on"
                    @click="$tours['appTour'].start()"
                >
                    <v-icon>fal fa-question-circle</v-icon>
                </v-btn>
            </template>
            <span>{{ $t('tour.guide') }}</span>
        </v-tooltip> -->

        <div class="right-btn-container">
            <connection-management-button />

            <div v-if="user" class="logged-user">{{ user.name }}</div>

            <v-menu bottom transition="slide-y-transition">
                <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon>
                        <v-icon>far fa-ellipsis-v</v-icon>
                    </v-btn>
                </template>

                <v-list v-if="user" class="user-menu">
                    <v-list-item :key="1" class="label">
                        <v-list-item-avatar>
                            <v-avatar class="primary" size="40">
                                <v-icon dark>fal fa-user</v-icon>
                            </v-avatar>
                        </v-list-item-avatar>

                        <v-list-item-content>
                            <v-list-item-title>{{ user.name }}</v-list-item-title>
                            <v-list-item-subtitle>{{ translateUserRole(user.role) }}</v-list-item-subtitle>
                        </v-list-item-content>
                    </v-list-item>

                    <v-divider />

                    <v-list-item
                        v-if="true"
                        :key="2"
                        :disabled="tableMode && tableMode !== 'transfer'"
                        @click="setTableMode('transfer')"
                    >
                        <v-list-item-action class="minw25 ml-1">
                            <v-icon
                                :class="{
                                    'icon-disabled': tableMode && tableMode !== 'transfer',
                                    'selected': tableMode === 'transfer'
                                }"
                            >
                                fal fa-exchange
                            </v-icon>
                        </v-list-item-action>
                        <v-list-item-title :class="{'selected': tableMode === 'transfer'}">
                            {{ $t('tables.transfer') }}
                        </v-list-item-title>
                    </v-list-item>

                    <v-list-item
                        v-if="true"
                        :key="3"
                        color="primary"
                        :disabled="tableMode && tableMode !== 'handOverTable'"
                        @click="setTableMode('handOverTable')"
                    >
                        <v-list-item-action class="minw25 ml-1">
                            <v-icon
                                :class="{
                                    'icon-disabled': tableMode && tableMode !== 'handOverTable',
                                    'selected': tableMode === 'handOverTable'
                                }"
                            >
                                fal fa-people-carry
                            </v-icon>
                        </v-list-item-action>
                        <v-list-item-title :class="{'selected': tableMode === 'handOverTable'}">
                            {{ $t('tables.hand_over_table') }}
                        </v-list-item-title>
                    </v-list-item>

                    <v-list-item
                        v-if="locale === 'sr'"
                        :key="4"
                        :class="{ 'primary--text': trainingMode }"
                        @click="toggleTrainingMode"
                    >
                        <v-list-item-action class="minw25 ml-1">
                            <v-icon>fal fa-chalkboard-teacher</v-icon>
                        </v-list-item-action>
                        <v-list-item-title>{{ trainingMode ? $t('general.training_stop') : $t('general.training_start') }}</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                        v-if="allowKitchen"
                        :key="5"
                        @click="$router.push({ name: 'kitchen', query: { from: 'tables' } }).catch(err => {})"
                    >
                        <v-list-item-action class="minw25 ml-1">
                            <v-icon>fal fa-clipboard-list</v-icon>
                        </v-list-item-action>
                        <v-list-item-title>{{ $t('general.kitchen') }}</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                        v-if="allows('nav_access_admin')"
                        :key="6"
                        @click="$router.push({ name: 'adminDashboard' }).catch(err => {})"
                    >
                        <v-list-item-action class="minw25 ml-1">
                            <v-icon>fal fa-sliders-h</v-icon>
                        </v-list-item-action>
                        <v-list-item-title>{{ $t('general.administration') }}</v-list-item-title>
                    </v-list-item>

                    <v-divider />

                    <v-list-item :key="7" @click="clockOut">
                        <v-list-item-action class="minw25 ml-1">
                            <v-icon>fal fa-user-clock</v-icon>
                        </v-list-item-action>
                        <v-list-item-title>{{ $t('general.clock_out') }}</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
        </div>
    </v-app-bar>
</template>

<script>
import UserService from '@/services/UserService';
import Sector from '@/models/Sector';
import Terminal from '@/models/Terminal';
import ConnectionManagementButton from '@/components/ConnectionManagementButton';
import { mapGetters } from 'vuex';
import * as mutationTypes from '@/store/mutationTypes';

export default {
    name: 'TablesAppBar',

    components: {
        ConnectionManagementButton
    },

    created() {
        this.currentTab = `tab-${this.activeSector}`
    },

    beforeDestroy() {
        this.setTableMode(null);
    },

    data: () => ({
        currentTab: null,
        refreshing: null
    }),

    computed: {
        ...mapGetters(['trainingMode', 'tableMode', 'modules', 'impersonate', 'activeSector']),

        sectors() {
            return Sector.all()
        },

        appBarBackground() {
            if(this.trainingMode) return '#ff9e0d';
            if(this.tableMode) return '#FF5252';
            return '#1565c0';
        },

        allowKitchen() {
            const kdsRegistered = Terminal.all().some(t => t.isKitchenDisplaySystem())
            return this.modules.kds && kdsRegistered
        }
    },

    watch: {
        currentTab(val) {
            this.eventBus.$emit('tables-show-tab', val)

            const activeSector = val?.split('-')[1]
            if(activeSector) {
                this.$store.commit(mutationTypes.SET_ACTIVE_SECTOR, { activeSector: +activeSector })
            }
        }
    },

    methods: {
        translateUserRole(role) {
            // if default role and name translation exists use it
            const transKey = `user_roles.default.${role.name}`
            if(this.$t(transKey) !== transKey && role.id <= 4) {
                return this.$t(transKey)
            }

            return role.name
        },

        toggleTrainingMode() {
            this.$store.commit(mutationTypes.SET_TRAINING_MODE, { trainingMode: !this.trainingMode });
        },

        setTableMode(mode = null) {
            if(mode === 'transfer' && !this.isOnline) {
                this.$snackbar(this.$t('connection.internet_offline_message_not_allowed'), 'error');
                return;
            }

            this.$store.commit(mutationTypes.SET_TABLE_MODE, { tableMode: !this.tableMode ? mode : null });
        },

        clockOut() {
            this.$confirm(
                this.$t('tables.clock_out_confirm'),
                {
                    buttonTrueText: this.$t('general.yes'),
                    buttonFalseText: this.$t('general.no'),
                    icon: 'fal fa-times',
                    color: 'info',
                    title: this.$t('general.clock_out'),
                    persistent: true,
                },
                async result => {
                    if(result === null) return;

                    if(!result) {
                        UserService.clockOut();
                        this.$router.push({ name: 'userLogin' }).catch(err => {});
                    } else {
                        this.setTableMode('handOverShift')
                    }
                }
            );
        },

        refresh() {
            // if(locale !== 'me') {
            //     this.eventBus.$emit('orders-refresh')
            //     this.refreshing = 'inProgress'
            //     setTimeout(() => (this.refreshing = 'done'), 650)
            //     setTimeout(() => (this.refreshing = null), 20000)
            //     return;
            // }

            if(!this.isOnline) {
                this.$snackbar(this.$t('connection.internet_offline_message_not_allowed'), 'error');
                return
            }

            if(this.device.platform === 'android') {
                this.eventBus.$emit('orders-refresh')
                this.refreshing = 'inProgress'
                setTimeout(() => (this.refreshing = 'done'), 650)
                setTimeout(() => (this.refreshing = null), 20000)
            } else {
                location.reload()
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.header {
    ::v-deep .v-toolbar__content {
        padding: 0;
        justify-content: space-between;
    }

    .left-btn-container {
        width: 50px;
    }

    .tabs {
        margin: 0 4px;
        width: auto;

        @media screen and (max-width: 762px) {
            width: 100%;
            max-width: calc(100% - 160px);
        }

        .first-tab {
            margin-left: auto !important;
        }

        ::v-deep .v-slide-group__prev {
            display: none !important;
        }
    }

    .right-btn-container {
        padding: 0 4px;
        min-width: 100px;
        display: flex;
        justify-content: flex-end;
        align-items: center;

        .logged-user {
            margin: 0 8px;
            font-size: 15px;
            white-space: nowrap;

            @media screen and (max-width: 762px) {
                display: none;
            }
        }

        .user-menu {
            padding: 0;
            font-size: 16px;

            .label {
                background: #f0f0f0;
            }
        }

        .icon-disabled {
            opacity: 0.5;
        }

        .selected {
            color: var(--v-primary-base);
        }
    }
}
</style>
